/**
 * PipelineTracker Page
 *
 * Content Pipeline Tracker - "Mission Control" dashboard for CEO and Content Managers.
 * Track content pipeline status across all sites and categories.
 *
 * Date: 2026-03-11
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, RefreshCw, Target, Filter, Clock } from 'lucide-react';
import { TargetCard } from '@/components/pipeline-tracker/TargetCard';
import { AddTargetDialog } from '@/components/pipeline-tracker/AddTargetDialog';
import {
  getTargets,
  getSites,
  type PipelineTarget,
  type Site,
} from '@/services/pipelineTrackerService';

export default function PipelineTracker() {
  const [targets, setTargets] = useState<PipelineTarget[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);

  // Filters
  const [filterSiteId, setFilterSiteId] = useState<string>('all');
  const [filterActive, setFilterActive] = useState<string>('active');

  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const loadData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);

    try {
      const [targetsResult, sitesResult] = await Promise.all([
        getTargets({
          siteId: filterSiteId === 'all' ? undefined : filterSiteId,
          isActive: filterActive === 'all' ? 'all' : filterActive === 'active',
        }),
        getSites(),
      ]);

      if (targetsResult.success) {
        setTargets(targetsResult.targets);
      }
      if (sitesResult.success) {
        setSites(sitesResult.sites);
      }
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load pipeline tracker data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterSiteId, filterActive]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadData(true);
    }, 60000);

    return () => clearInterval(interval);
  }, [loadData]);

  const handleRefresh = () => loadData(true);

  const handleTargetCreated = () => {
    loadData();
  };

  const getStatusSummary = () => {
    const total = targets.length;
    const complete = targets.filter(t => t.status.overallProgress >= 95).length;
    const inProgress = targets.filter(t => t.status.overallProgress > 0 && t.status.overallProgress < 95).length;
    const blocked = targets.filter(t => t.status.blockedReason).length;
    const discoveryReady = targets.filter(t => t.status.discoveryReady).length;

    return { total, complete, inProgress, blocked, discoveryReady };
  };

  const summary = getStatusSummary();

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-6 w-6" />
            Content Pipeline Tracker
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Mission Control for tracking content pipeline status across sites and categories
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Target
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{summary.total}</div>
            <div className="text-sm text-gray-500">Total Targets</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">{summary.complete}</div>
            <div className="text-sm text-gray-500">Complete (95%+)</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-blue-600">{summary.inProgress}</div>
            <div className="text-sm text-gray-500">In Progress</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-amber-600">{summary.blocked}</div>
            <div className="text-sm text-gray-500">Blocked</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-purple-600">{summary.discoveryReady}</div>
            <div className="text-sm text-gray-500">Discovery Ready</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium">Filter:</span>
        </div>
        <Select value={filterSiteId} onValueChange={setFilterSiteId}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Sites" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sites</SelectItem>
            {sites.map((site) => (
              <SelectItem key={site.id} value={site.id}>
                {site.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterActive} onValueChange={setFilterActive}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active Only</SelectItem>
            <SelectItem value="inactive">Inactive Only</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Clock className="h-3 w-3" />
          Last refresh: {lastRefresh.toLocaleTimeString()}
        </div>
      </div>

      {/* Targets List */}
      {targets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No targets yet</h3>
            <p className="text-gray-500 mb-4">
              Create your first target to start tracking content pipeline progress.
            </p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Target
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {targets.map((target) => (
            <TargetCard
              key={target.id}
              target={target}
              onRefresh={handleRefresh}
            />
          ))}
        </div>
      )}

      {/* Add Target Dialog */}
      <AddTargetDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSuccess={handleTargetCreated}
      />
    </div>
  );
}
